import { Alert, Modal, Tag } from 'antd';
import type { LLMConfigValidation } from '@/types';
import { diffJsonPaths } from '@/utils/llmConfigDocument';

interface Props {
  open: boolean;
  title: string;
  before: unknown;
  after: unknown;
  validation: LLMConfigValidation | null;
  publishing: boolean;
  deploymentPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  showAccessPreview?: boolean;
}

function displayValue(value: unknown): string {
  if (value === undefined) return '未设置';
  const encoded = JSON.stringify(value);
  return encoded && encoded.length > 100
    ? `${encoded.slice(0, 100)}…`
    : encoded || String(value);
}

export function AppConfigPublishDialog({
  open,
  title,
  before,
  after,
  validation,
  publishing,
  deploymentPending,
  onCancel,
  onConfirm,
  showAccessPreview = false,
}: Props) {
  const changes = diffJsonPaths(before, after);
  const errors =
    validation?.issues.filter((issue) => issue.severity === 'error') || [];
  const warnings =
    validation?.issues.filter((issue) => issue.severity === 'warning') || [];

  return (
    <Modal
      open={open}
      title={title}
      width={760}
      okText="确认发布"
      cancelText="取消"
      confirmLoading={publishing}
      okButtonProps={{
        disabled: deploymentPending || !validation?.ok || changes.length === 0,
      }}
      onCancel={onCancel}
      onOk={onConfirm}
    >
      <div className="space-y-4">
        {deploymentPending ? (
          <Alert
            type="warning"
            showIcon
            message="当前配置仍在部署或回滚，完成前不能再次发布"
          />
        ) : null}
        <Alert
          type={errors.length > 0 ? 'error' : 'success'}
          showIcon
          message={
            errors.length > 0
              ? `${errors.length} 个阻断问题`
              : `校验通过：${validation?.priced_routes ?? 0}/${validation?.selectable_routes ?? 0} 条可选路由有价格`
          }
        />
        {errors.length > 0 || warnings.length > 0 ? (
          <div className="max-h-48 space-y-2 overflow-auto rounded border p-3 text-sm">
            {[...errors, ...warnings].map((issue, index) => (
              <div key={`${issue.code}-${issue.path || ''}-${index}`}>
                <Tag color={issue.severity === 'error' ? 'red' : 'orange'}>
                  {issue.code}
                </Tag>
                <span>{issue.message}</span>
                {issue.path ? (
                  <span className="ml-2 font-mono text-xs text-muted-foreground">
                    {issue.path}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
        {showAccessPreview && validation?.access_previews?.length ? (
          <div>
            <div className="mb-2 font-medium">Access glob 命中预览</div>
            <div className="max-h-48 space-y-2 overflow-auto rounded border p-3 text-xs">
              {validation.access_previews.map((preview) => (
                <div key={preview.path} className="rounded bg-slate-50 p-2">
                  <code>{preview.path}</code>
                  <div>
                    状态：{preview.enabled ? '启用' : '停用'} · 模型：
                    {preview.matched_models.join(', ') || '-'}
                  </div>
                  <div>
                    Provider：{preview.matched_providers.join(', ') || '-'} ·
                    Route：{preview.matched_routes.join(', ') || '-'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        <div>
          <div className="mb-2 font-medium">配置 Diff（{changes.length}）</div>
          <div className="max-h-64 overflow-auto rounded border">
            {changes.length === 0 ? (
              <div className="p-4 text-muted-foreground">没有待发布变更</div>
            ) : (
              changes.map((change) => (
                <div
                  key={change.path}
                  className="grid grid-cols-[220px_1fr] gap-3 border-b p-2 text-xs last:border-b-0"
                >
                  <code>{change.path}</code>
                  <div>
                    <span className="text-red-600">
                      {displayValue(change.before)}
                    </span>
                    <span className="mx-2">→</span>
                    <span className="text-green-700">
                      {displayValue(change.after)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
